import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Spinner, Link as LinkIcon, NotePencil, Warning, Command, ArrowFatUp, ArrowElbowDownLeft, ArrowRight } from '@phosphor-icons/react';
import { X } from 'lucide-react';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { analytics, capPeriodId } from '@/lib/analytics';

// Sentinel raised by the Postgres BEFORE INSERT trigger enforce_saves_cap.
// Bubbles up as `Database error: solem_save_cap_exceeded`.
const CAP_SENTINEL = 'solem_save_cap_exceeded';

function safeHost(u: string): string | undefined {
  try { return new URL(u).hostname; } catch { return undefined; }
}

interface ScrapeResponse {
  success: boolean;
  data?: {
    url: string;
    canonical_url: string | null;
    title: string | null;
    description: string | null;
    image: string | null;
    image_source: string | null;
    image_storage_path: string | null;
    favicon: string | null;
    favicon_storage_path: string | null;
    site_name: string | null;
    type: string | null;
    author: string | null;
    content: string | null;
    html_archive: string | null;
    published_at: string | null;
  };
  error?: string;
  code?: string;
}

interface AddWebpageModalV2Props {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AddWebpageModalV2({ trigger, open: controlledOpen, onOpenChange }: AddWebpageModalV2Props) {
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capHit, setCapHit] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  
  // Detect if on actual mobile device (iOS/Android) for bottom positioning
  const isMobileDevice = useIsMobileDevice();
  
  // Track visual viewport height for keyboard-aware positioning on mobile
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  
  // Support both controlled and uncontrolled modes
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    }
    if (controlledOpen === undefined) {
      setInternalOpen(value);
    }
  };

  // Modal open/close lifecycle analytics. We fire on the transition, not on
  // every render, so the controlled/uncontrolled mix doesn't double-fire.
  const openedAtRef = useRef<number | null>(null);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      openedAtRef.current = Date.now();
      analytics.track('add_modal_opened', {});
    } else if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
      analytics.track('add_modal_closed', {
        duration_ms: openedAtRef.current ? Date.now() - openedAtRef.current : undefined,
      });
      openedAtRef.current = null;
    }
  }, [isOpen]);

  // Track visual viewport height for keyboard-aware positioning on mobile
  useEffect(() => {
    if (!isMobileDevice || !isOpen) {
      setViewportHeight(null);
      return;
    }
    
    const updateHeight = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      }
    };
    
    // Initialize viewport height
    updateHeight();
    
    // Listen for viewport changes (keyboard show/hide)
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', updateHeight);
      viewport.addEventListener('scroll', updateHeight);
      
      return () => {
        viewport.removeEventListener('resize', updateHeight);
        viewport.removeEventListener('scroll', updateHeight);
      };
    }
  }, [isMobileDevice, isOpen]);

  // Handle keyboard shortcuts on the container (not document level)
  const handleContainerKeyDown = (e: React.KeyboardEvent) => {
    // Escape - close the box (works from anywhere)
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }
    
    // Cmd/Ctrl + Shift + Enter to save
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
      return;
    }
    
    // Let all other key events pass through naturally (Cmd+A, arrows, etc.)
  };

  // Focus input when opened
  useEffect(() => {
    if (isOpen && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSubmit = async () => {
    setHasAttemptedSave(true);
    
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;
    
    // Check if URL is invalid (e.g., has spaces)
    if (urlValidation.status === 'invalid') {
      setError(urlValidation.errorMessage || 'Please enter a valid URL');
      return;
    }
    
    // Validate URL format before submitting
    let urlToSave = trimmedUrl;
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      urlToSave = `https://${trimmedUrl}`;
    }
    
    try {
      new URL(urlToSave);
    } catch {
      setError('Please enter a valid URL');
      return;
    }
    
    // Check if URL has a valid domain
    if (urlValidation.status !== 'valid') {
      setError('Please enter a valid URL');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('You must be signed in to save webpages');
      }

      // Get session for access token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        throw new Error('Session expired. Please sign in again.');
      }

      // Generate webpage ID client-side (used for storage paths)
      const webpageId = crypto.randomUUID();

      let response: Response;
      try {
        response = await fetch('/api/scrape-with-upload', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            url: urlToSave,
            userId: user.id,
            webpageId,
          }),
        });
      } catch {
        throw new Error('Network error: Could not connect to server');
      }

      let result: ScrapeResponse;
      try {
        result = await response.json();
      } catch {
        throw new Error(`Server error (${response.status}): Invalid response`);
      }

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch webpage');
      }

      const { error: insertError } = await supabase
        .from('webpages')
        .insert({
          id: webpageId,  // Use pre-generated ID to match storage paths
          user_id: user.id,
          url: result.data.url,  // URL after redirects (resolved by server)
          canonical_url: result.data.canonical_url,
          title: result.data.title,
          description: result.data.description,
          image: result.data.image,
          image_source: result.data.image_source || null,
          image_storage_path: result.data.image_storage_path || null,
          favicon: result.data.favicon,
          favicon_storage_path: result.data.favicon_storage_path || null,
          site_name: result.data.site_name,
          type: result.data.type,
          content: result.data.content,
          html_archive: result.data.html_archive || null,
          author: result.data.author,
          published_at: result.data.published_at,
          notes: notes.trim() || null,
          save_source: 'app',
          client_version: (import.meta.env.VITE_APP_VERSION as string | undefined) || '0.0.0',
        });

      if (insertError) {
        throw new Error(`Database error: ${insertError.message}`);
      }

      analytics.track('save_created', {
        save_id: webpageId,
        via: 'web_modal',
        has_note: notes.trim().length > 0,
        url_host: safeHost(result.data.url),
      });

      toast({
        title: "Saved!",
        description: result.data.title || "Webpage saved successfully",
      });

      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save webpage";
      console.error('Error saving webpage:', message);
      if (message.includes(CAP_SENTINEL)) {
        setCapHit(true);
        setError(null);
        analytics.track('cap_hit', { cap_type: 'saves', surface: 'add_modal', cap_period_id: capPeriodId() });
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    handleClose();
    navigate('/upgrade');
  };

  const handleClose = () => {
    setUrl('');
    setNotes('');
    setError(null);
    setHasBlurred(false);
    setHasAttemptedSave(false);
    setCapHit(false);
    setIsOpen(false);
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    if (error) setError(null);
    // Reset validation state when user starts typing again
    if (hasBlurred) setHasBlurred(false);
    if (hasAttemptedSave) setHasAttemptedSave(false);
  };

  const handleUrlBlur = () => {
    // Only set blurred if there's content
    if (url.trim()) {
      setHasBlurred(true);
    }
  };

  // URL validation and domain extraction
  // Returns status: 'empty' | 'typing' | 'pending' | 'valid' | 'invalid'
  const urlValidation = useMemo(() => {
    const trimmed = url.trim();
    
    // Empty - no badge
    if (!trimmed) {
      return { status: 'empty' as const, domain: null, showBadge: false, errorMessage: null };
    }
    
    // Check if it looks like they're typing the protocol (h, ht, htt, http, https, http:, https:, http:/, https:/, http://, https://)
    const looksLikeIncompleteProtocol = /^h(t(t(p(s?(:(\/?\/?)?)?)?)?)?)?\s*$/i.test(trimmed);
    
    if (looksLikeIncompleteProtocol) {
      return { status: 'typing' as const, domain: null, showBadge: false, errorMessage: null };
    }
    
    // Check if they have the full protocol but nothing after it
    if (/^https?:\/\/\s*$/i.test(trimmed)) {
      return { status: 'typing' as const, domain: null, showBadge: false, errorMessage: null };
    }
    
    // Check for spaces ANYWHERE in the URL - this is always invalid
    if (trimmed.includes(' ')) {
      // Try to extract domain for display
      let displayDomain = trimmed;
      try {
        const urlWithoutSpaces = trimmed.replace(/ /g, '');
        let urlToParse = urlWithoutSpaces;
        if (!urlWithoutSpaces.startsWith('http://') && !urlWithoutSpaces.startsWith('https://')) {
          urlToParse = `https://${urlWithoutSpaces}`;
        }
        const parsed = new URL(urlToParse);
        displayDomain = parsed.hostname.replace('www.', '');
      } catch {
        // Just use what we have
        displayDomain = trimmed.split(' ')[0].replace(/^https?:\/\//i, '').split('/')[0];
      }
      return { status: 'invalid' as const, domain: displayDomain || trimmed, showBadge: true, errorMessage: 'URL cannot contain spaces' };
    }
    
    // Try to parse as URL
    let urlToParse = trimmed;
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      // If it contains a dot, assume https and try to parse
      if (trimmed.includes('.')) {
        urlToParse = `https://${trimmed}`;
      } else {
        // No protocol and no dot - still typing, show as pending (gray)
        return { status: 'pending' as const, domain: trimmed, showBadge: true, errorMessage: null };
      }
    }
    
    try {
      const parsed = new URL(urlToParse);
      const domain = parsed.hostname.replace('www.', '');
      
      // If domain is empty or just the protocol, still typing
      if (!domain || domain === '') {
        return { status: 'typing' as const, domain: null, showBadge: false, errorMessage: null };
      }
      
      // Check if domain is valid (has at least one dot and content after it)
      const hasValidDomain = domain.includes('.') && !domain.endsWith('.');
      
      if (!hasValidDomain) {
        // Has a dot but not complete - pending state
        return { status: 'pending' as const, domain, showBadge: true, errorMessage: null };
      }
      
      // Valid URL!
      return { status: 'valid' as const, domain, showBadge: true, errorMessage: null };
    } catch {
      // Can't parse - but might still be typing, show as pending unless user has blurred/tried to save
      // Extract what looks like a domain from the input
      const afterProtocol = trimmed.replace(/^https?:\/\//i, '');
      if (!afterProtocol) {
        return { status: 'typing' as const, domain: null, showBadge: false, errorMessage: null };
      }
      return { status: 'pending' as const, domain: afterProtocol, showBadge: true, errorMessage: null };
    }
  }, [url]);

  // Determine if we should show error state (red badge)
  // Only show error when: user has blurred, tried to save, there's an API error, or there's an obvious format error
  const shouldShowError = useMemo(() => {
    // If there's an API error after trying to save, show error state
    if (error && hasAttemptedSave) {
      return true;
    }
    
    if (urlValidation.status === 'valid' || urlValidation.status === 'empty' || urlValidation.status === 'typing') {
      return false;
    }
    
    // Immediate invalid (like spaces in URL)
    if (urlValidation.status === 'invalid') {
      return true;
    }
    
    // If user has blurred or attempted to save, show error for pending URLs
    if (hasBlurred || hasAttemptedSave) {
      return urlValidation.status === 'pending';
    }
    
    // Check for obvious format errors that we can show immediately
    const trimmed = url.trim();
    
    // Malformed protocol (like "htttp://" or "htp://")
    if (/^h[t]{0,2}p[s]?:\/\//i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
      return true;
    }
    
    return false;
  }, [urlValidation, hasBlurred, hasAttemptedSave, url, error]);

  const isMac = typeof navigator !== 'undefined' && navigator.platform?.toUpperCase().includes('MAC');
  const canSave = urlValidation.status === 'valid' && !isLoading && !error;

  return (
    <>
      {trigger && (
        <div onClick={() => setIsOpen(true)}>
          {trigger}
        </div>
      )}

      {/* Floating smart box - rendered via portal to escape any container constraints */}
      {isOpen && createPortal(
        <div 
          className={`fixed z-[9999] ${isMobileDevice ? 'inset-x-0 top-0' : 'inset-0 flex items-center justify-center'}`}
          style={isMobileDevice && viewportHeight ? { height: `${viewportHeight}px` } : undefined}
        >
          {/* Click-outside overlay (invisible) */}
          <div 
            className="absolute inset-0"
            onClick={handleClose}
          />
          {/* Floating card - bottom positioned on mobile devices, centered on desktop */}
          <div
            ref={containerRef}
            onKeyDown={handleContainerKeyDown}
            className={`bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 shadow-lg ${capHit ? 'px-6 py-5' : 'px-4 py-4'} ${
              isMobileDevice
                ? `absolute bottom-3 left-3 right-3 rounded-sm animate-panel-slide-up`
                : `relative w-full ${capHit ? 'max-w-xl' : 'max-w-lg'} mx-4 rounded-sm animate-panel-enter`
            }`}
          >
            {capHit ? (
              <>
                {/* Cap-hit state — replaces the form */}
                <div className="flex items-start gap-3">
                  <div className="flex-none rounded-sm bg-red-50 ring-1 ring-red-200 p-2">
                    <Warning className="h-5 w-5 text-red-600" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                      Saver cap reached
                    </h2>
                    <p className="text-sm font-light text-neutral-600 dark:text-neutral-300 mt-1 leading-relaxed">
                      You've saved 100 of 100 webpages on the Saver plan. Upgrade to Pro for unlimited saves, 500 AI chats/month, and unlimited MCP calls — or delete some saves to free space.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-5">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleClose}
                    className="rounded-sm bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-600 dark:text-neutral-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleUpgrade}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5"
                  >
                    Upgrade to Pro
                    <ArrowRight className="h-3.5 w-3.5" weight="bold" />
                  </Button>
                </div>
              </>
            ) : (
            <>
            {/* Link input section */}
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10">
                <LinkIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" weight="bold" />
              </div>
              <Input
                ref={urlInputRef}
                type="text"
                placeholder="Paste or type a URL..."
                value={url}
                onChange={handleUrlChange}
                onBlur={handleUrlBlur}
                onKeyDown={(e) => {
                  // Explicitly handle Cmd/Ctrl+A to select all text
                  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
                    e.currentTarget.select();
                  }
                }}
                disabled={isLoading}
                tabIndex={1}
                className={`h-12 text-base font-light pl-10 pr-10 bg-neutral-200 dark:bg-neutral-900 rounded-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none shadow-none border-0 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 ${error ? 'ring-2 ring-destructive' : ''}`}
              />
              {url && (
                <button
                  onClick={() => setUrl('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                  type="button"
                  tabIndex={4}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Notes section */}
            <div className="mt-3">
              <div className="relative">
                <div className="absolute left-3.5 top-3.5 z-10">
                  <NotePencil className="h-4 w-4 text-neutral-500 dark:text-neutral-400" weight="bold" />
                </div>
                <Textarea
                  placeholder="Add notes, tags, or reminders..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onKeyDown={(e) => {
                    // Explicitly handle Cmd/Ctrl+A to select all text
                    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
                      e.currentTarget.select();
                    }
                  }}
                  disabled={isLoading}
                  rows={2}
                  tabIndex={2}
                  className="resize-none text-base font-light pl-10 bg-neutral-200 dark:bg-neutral-900 rounded-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none shadow-none border-0 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 min-h-[72px]"
                />
              </div>
            </div>

            {/* Footer with URL pill, error message, and save button */}
            <div className="flex items-center justify-between gap-3 mt-3 pt-1">
              <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                {/* Cancel button - shows when no URL data, replaced by badge when typing */}
                {(!urlValidation.showBadge || !urlValidation.domain) && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleClose}
                      className="rounded-sm bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-600 dark:text-neutral-300"
                    >
                      Cancel
                    </Button>
                    <span className="text-xs text-neutral-500 hidden sm:inline">
                      ESC
                    </span>
                  </div>
                )}
                {/* URL Badge - gray (pending), blue (valid), or red (error) */}
                {urlValidation.showBadge && urlValidation.domain && (
                  <span 
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-full transition-colors min-w-0 max-w-[50%] flex-shrink-0 ${
                      urlValidation.status === 'valid' && !shouldShowError
                        ? 'bg-primary/10 text-primary' 
                        : shouldShowError
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
                    }`}
                  >
                    {shouldShowError ? (
                      <Warning className="w-3.5 h-3.5 flex-shrink-0" weight="bold" />
                    ) : (
                      <LinkIcon className="w-3.5 h-3.5 flex-shrink-0" weight="bold" />
                    )}
                    <span className="truncate">{urlValidation.domain}</span>
                  </span>
                )}
                {/* Show error message - either validation error or API error */}
                {shouldShowError && (
                  <span className="text-xs text-destructive hidden sm:inline truncate min-w-0">
                    {urlValidation.errorMessage || error || 'Invalid URL'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-neutral-500 hidden sm:inline-flex items-center gap-0.5">
                  {isMac ? (
                    <>
                      <Command className="h-3 w-3" weight="bold" />
                      <ArrowFatUp className="h-3 w-3" weight="bold" />
                      <ArrowElbowDownLeft className="h-3 w-3" weight="bold" />
                    </>
                  ) : (
                    <span className="text-xs">Ctrl+Shift+Enter</span>
                  )}
                </span>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!canSave}
                  tabIndex={3}
                  className="min-w-[70px] rounded-sm"
                >
                  {isLoading ? (
                    <Spinner className="h-4 w-4 animate-spin" />
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>
            </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
